import { tr as __tr, useLocale as __useLocale, direction as __direction, localizedField as __localizedField } from "../../i18n/runtime";
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CommerceSettings from './CommerceSettings';
import CurrencySelect from '../../components/CurrencySelect';
import { Save, Mail, CreditCard, Globe, Building, TestTube } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../i18n/LanguageContext';
const API = '/api';
const SettingsPage = () => {
  __useLocale();
  const {
    token
  } = useAuth();
  const {
    language
  } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [companySettings, setCompanySettings] = useState({});
  const [emailSettings, setEmailSettings] = useState({});
  const [paymentSettings, setPaymentSettings] = useState({
    paypal_enabled: false,
    paypal_mode: 'sandbox',
    paypal_client_id: '',
    paypal_client_secret: '',
    bank_transfer_enabled: true,
    bank_name: '',
    bank_account_number: '',
    bank_iban: '',
    cash_enabled: true
  });
  const [apiSettings, setApiSettings] = useState({
    hotel_api_enabled: false,
    hotel_api_provider: '',
    hotel_api_key: '',
    flight_api_enabled: false,
    flight_api_provider: '',
    flight_api_key: '',
    bus_api_enabled: false
  });
  const [loadError, setLoadError] = useState(false);
  const headers = {
    Authorization: `Bearer ${token}`
  };
  useEffect(() => {
    fetchSettings();
  }, []);
  const fetchSettings = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [company, email, api] = await Promise.all([axios.get(`${API}/settings.php?category=company`, {
        headers
      }), axios.get(`${API}/settings.php?category=email`, {
        headers
      }), axios.get(`${API}/settings.php?category=api`, {
        headers
      })]);
      setCompanySettings(prev => ({
        ...prev,
        ...company.data
      }));
      setEmailSettings(prev => ({
        ...prev,
        ...email.data
      }));
      setApiSettings(prev => ({
        ...prev,
        ...api.data
      }));
    } catch (error) {
      setLoadError(true);
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };
  const saveCompanySettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings.php?category=company`, companySettings, {
        headers
      });
      setMessage(language === 'ar' ? "تم حفظ إعدادات الشركة" : 'Company settings saved');
    } catch (error) {
      setMessage(language === 'ar' ? "خطأ في الحفظ" : 'Error saving');
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };
  const savePaymentSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings.php?category=payment`, paymentSettings, {
        headers
      });
      setMessage(language === 'ar' ? "تم حفظ إعدادات الدفع" : 'Payment settings saved');
    } catch (error) {
      setMessage(language === 'ar' ? "خطأ في الحفظ" : 'Error saving');
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };
  const saveEmailSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings.php?category=email`, emailSettings, {
        headers
      });
      setMessage(language === 'ar' ? "تم حفظ إعدادات البريد" : 'Email settings saved');
    } catch (error) {
      setMessage(language === 'ar' ? "خطأ في الحفظ" : 'Error saving');
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };
  const testEmailSettings = async () => {
    if (!testEmail) return;
    try {
      await axios.post(`${API}/email/test.php?test_email=${encodeURIComponent(testEmail)}`, {}, {
        headers
      });
      setMessage(language === 'ar' ? "تم إرسال بريد الاختبار" : 'Test email sent');
    } catch (error) {
      setMessage(language === 'ar' ? "فشل إرسال البريد" : 'Failed to send email');
    }
    setTimeout(() => setMessage(''), 3000);
  };
  const saveApiSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings.php?category=api`, apiSettings, {
        headers
      });
      setMessage(language === 'ar' ? "تم حفظ إعدادات API" : 'API settings saved');
    } catch (error) {
      setMessage(language === 'ar' ? "خطأ في الحفظ" : 'Error saving');
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };
  if (loadError && !loading) return <div role="alert">{__tr("تعذر تحميل الإعدادات. ")}<button onClick={fetchSettings}>{__tr("إعادة المحاولة")}</button></div>;
  if (loading) {
    return <div className="flex justify-center items-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6B5B95]"></div></div>;
  }
  return <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-800">{__tr(language === 'ar' ? "الإعدادات" : 'Settings')}</h1>

      {__tr(message && <div className="bg-green-100 text-green-700 px-4 py-3 rounded-lg">{__tr(message)}</div>)}

      <Tabs defaultValue="company" className="w-full">
        <TabsList className="flex flex-wrap h-auto w-full max-w-2xl gap-1">
          <TabsTrigger value="company"><Building className="w-4 h-4 ml-2" />{__tr(language === 'ar' ? "الشركة" : 'Company')}</TabsTrigger>
          <TabsTrigger value="payment"><CreditCard className="w-4 h-4 ml-2" />{__tr(language === 'ar' ? "الدفع" : 'Payment')}</TabsTrigger>
          <TabsTrigger value="currencies">{__tr("العملات")}</TabsTrigger>
          <TabsTrigger value="email"><Mail className="w-4 h-4 ml-2" />{__tr(language === 'ar' ? "البريد" : 'Email')}</TabsTrigger>
          <TabsTrigger value="api"><Globe className="w-4 h-4 ml-2" />{__tr(language === 'ar' ? 'APIs' : 'APIs')}</TabsTrigger>
        </TabsList>

        {/* Company Settings */}
        <TabsContent value="company" className="bg-white rounded-xl shadow-lg p-6 mt-4">
          <h2 className="text-lg font-bold mb-6">{__tr(language === 'ar' ? "إعدادات الشركة" : 'Company Settings')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-2">{__tr("اسم الشركة (عربي)")}</label>
              <Input value={companySettings.company_name_ar} onChange={e => setCompanySettings({
              ...companySettings,
              company_name_ar: e.target.value
            })} />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-2">Company Name (English)</label>
              <Input value={companySettings.company_name_en} onChange={e => setCompanySettings({
              ...companySettings,
              company_name_en: e.target.value
            })} />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-2">{__tr(language === 'ar' ? "الهاتف" : 'Phone')}</label>
              <Input value={companySettings.company_phone} onChange={e => setCompanySettings({
              ...companySettings,
              company_phone: e.target.value
            })} />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-2">{__tr(language === 'ar' ? "البريد الإلكتروني" : 'Email')}</label>
              <Input value={companySettings.company_email} onChange={e => setCompanySettings({
              ...companySettings,
              company_email: e.target.value
            })} />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm text-gray-600 mb-2">{__tr(language === 'ar' ? "العنوان" : 'Address')}</label>
              <Input value={companySettings.company_address} onChange={e => setCompanySettings({
              ...companySettings,
              company_address: e.target.value
            })} />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-2">{__tr(language === 'ar' ? "العملة الافتراضية" : 'Default Currency')}</label>
              <p className="text-sm text-gray-600">{__tr("تُحدد من تبويب العملات.")}</p>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-2">{__tr(language === 'ar' ? "نسبة الضريبة %" : 'Tax Rate %')}</label>
              <Input type="number" value={companySettings.tax_rate} onChange={e => setCompanySettings({
              ...companySettings,
              tax_rate: parseFloat(e.target.value)
            })} />
            </div>
          </div>
          <Button onClick={saveCompanySettings} disabled={saving} className="mt-6 bg-[#6B5B95] hover:bg-[#5B4B85]">
            <Save className="w-4 h-4 ml-2" />{__tr(saving ? '...' : language === 'ar' ? "حفظ" : 'Save')}
          </Button>
        </TabsContent>

        <TabsContent value="currencies" className="bg-white rounded-xl p-6 mt-4"><CommerceSettings kind="currencies" /></TabsContent>
        {/* Payment Settings */}
        <TabsContent value="payment" className="bg-white rounded-xl shadow-lg p-6 mt-4">
          <CommerceSettings kind="gateways" />
        </TabsContent>

        {/* Email Settings */}
        <TabsContent value="email" className="bg-white rounded-xl shadow-lg p-6 mt-4">
          <h2 className="text-lg font-bold mb-6">{__tr(language === 'ar' ? "إعدادات Titan Email" : 'Titan Email Settings')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-2">SMTP Host</label>
              <Input value={emailSettings.smtp_host} onChange={e => setEmailSettings({
              ...emailSettings,
              smtp_host: e.target.value
            })} placeholder="smtp.titan.email" />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-2">SMTP Port</label>
              <Input type="number" value={emailSettings.smtp_port} onChange={e => setEmailSettings({
              ...emailSettings,
              smtp_port: parseInt(e.target.value)
            })} />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-2">SMTP Username</label>
              <Input value={emailSettings.smtp_user} onChange={e => setEmailSettings({
              ...emailSettings,
              smtp_user: e.target.value
            })} placeholder="your@email.com" />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-2">SMTP Password</label>
              <Input type="password" value={emailSettings.smtp_password} onChange={e => setEmailSettings({
              ...emailSettings,
              smtp_password: e.target.value
            })} />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-2">From Email</label>
              <Input value={emailSettings.from_email} onChange={e => setEmailSettings({
              ...emailSettings,
              from_email: e.target.value
            })} />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-2">From Name</label>
              <Input value={emailSettings.from_name} onChange={e => setEmailSettings({
              ...emailSettings,
              from_name: e.target.value
            })} />
            </div>
          </div>

          <div className="flex gap-4 mt-6">
            <Button onClick={saveEmailSettings} disabled={saving} className="bg-[#6B5B95] hover:bg-[#5B4B85]">
              <Save className="w-4 h-4 ml-2" />{__tr(saving ? '...' : language === 'ar' ? "حفظ" : 'Save')}
            </Button>
            <div className="flex gap-2">
              <Input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder={__tr(language === 'ar' ? "بريد الاختبار" : 'Test email')} className="w-48" />
              <Button onClick={testEmailSettings} variant="outline">
                <TestTube className="w-4 h-4 ml-2" />{__tr(language === 'ar' ? "اختبار" : 'Test')}
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* API Settings */}
        <TabsContent value="api" className="bg-white rounded-xl shadow-lg p-6 mt-4">
          <h2 className="text-lg font-bold mb-6">{__tr(language === 'ar' ? "إعدادات Booking APIs" : 'Booking API Settings')}</h2>

          {/* Hotel API */}
          <div className="border rounded-xl p-4 mb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-[#6B5B95]">{__tr(language === 'ar' ? "API الفنادق" : 'Hotels API')}</h3>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={apiSettings.hotel_api_enabled} onChange={e => setApiSettings({
                ...apiSettings,
                hotel_api_enabled: e.target.checked
              })} className="w-5 h-5" />
                <span>{__tr(language === 'ar' ? "تفعيل" : 'Enable')}</span>
              </label>
            </div>
            {__tr(apiSettings.hotel_api_enabled && <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-2">Provider</label>
                  <Input value={apiSettings.hotel_api_provider} onChange={e => setApiSettings({
                ...apiSettings,
                hotel_api_provider: e.target.value
              })} placeholder="booking.com, expedia..." />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-2">API Key</label>
                  <Input type="password" value={apiSettings.hotel_api_key} onChange={e => setApiSettings({
                ...apiSettings,
                hotel_api_key: e.target.value
              })} />
                </div>
              </div>)}
          </div>

          {/* Flight API */}
          <div className="border rounded-xl p-4 mb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-[#6B5B95]">{__tr(language === 'ar' ? "API الطيران" : 'Flights API')}</h3>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={apiSettings.flight_api_enabled} onChange={e => setApiSettings({
                ...apiSettings,
                flight_api_enabled: e.target.checked
              })} className="w-5 h-5" />
                <span>{__tr(language === 'ar' ? "تفعيل" : 'Enable')}</span>
              </label>
            </div>
            {__tr(apiSettings.flight_api_enabled && <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-2">Provider</label>
                  <Input value={apiSettings.flight_api_provider} onChange={e => setApiSettings({
                ...apiSettings,
                flight_api_provider: e.target.value
              })} placeholder="amadeus, sabre..." />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-2">API Key</label>
                  <Input type="password" value={apiSettings.flight_api_key} onChange={e => setApiSettings({
                ...apiSettings,
                flight_api_key: e.target.value
              })} />
                </div>
              </div>)}
          </div>

          <Button onClick={saveApiSettings} disabled={saving} className="mt-6 bg-[#6B5B95] hover:bg-[#5B4B85]">
            <Save className="w-4 h-4 ml-2" />{__tr(saving ? '...' : language === 'ar' ? "حفظ" : 'Save')}
          </Button>
        </TabsContent>
      </Tabs>
    </div>;
};
export default SettingsPage;
